/**
 * Referral share block (thank-you page).
 *
 * Uses the Web Share API where available — one tap into whichever app the
 * customer actually uses — and falls back to explicit WhatsApp / SMS / email
 * links plus copy-to-clipboard. All copy comes from data attributes rendered by
 * PHP so translation stays in the logineko text domain.
 */

const block = document.querySelector<HTMLElement>(".referral-share");

if (block) {
  const url = block.dataset.referralUrl ?? "";
  const title = block.dataset.shareTitle ?? "";
  const text = block.dataset.shareText ?? "";
  const copiedLabel = block.dataset.copiedLabel ?? "Copied";

  const nativeButton = block.querySelector<HTMLButtonElement>(
    ".referral-share__native"
  );
  const copyButton = block.querySelector<HTMLButtonElement>(
    ".referral-share__copy"
  );
  const targets = block.querySelector<HTMLElement>(".referral-share__targets");

  // Web Share is the better mobile path, so prefer it and hide the per-app row.
  if (nativeButton && typeof navigator.share === "function") {
    nativeButton.hidden = false;
    targets?.setAttribute("hidden", "hidden");

    // navigator.share() must be called synchronously from the user gesture;
    // awaiting anything first throws NotAllowedError.
    nativeButton.addEventListener("click", () => {
      navigator.share({ title, text, url }).catch((error: DOMException) => {
        // AbortError just means the user dismissed the share sheet — leave the
        // UI alone. Anything else means sharing is genuinely unavailable, so
        // restore the per-app row.
        if (error && error.name === "AbortError") {
          return;
        }
        targets?.removeAttribute("hidden");
      });
    });
  }

  if (copyButton) {
    // The button is icon-only, so the label lives in aria-label/title — that is
    // what has to carry the copied confirmation for screen readers and hover.
    const originalLabel = copyButton.getAttribute("aria-label") ?? "";

    copyButton.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Older Safari and any non-secure context: fall back to selecting the
        // field so the customer can copy manually.
        const field = block.querySelector<HTMLInputElement>(
          ".referral-share__url"
        );
        field?.select();
        return;
      }

      copyButton.setAttribute("aria-label", copiedLabel);
      copyButton.setAttribute("title", copiedLabel);
      copyButton.classList.add("is-copied");
      window.setTimeout(() => {
        copyButton.setAttribute("aria-label", originalLabel);
        copyButton.setAttribute("title", originalLabel);
        copyButton.classList.remove("is-copied");
      }, 2000);
    });
  }
}
